import type { Product } from '@/api/types'

export const TILE_COLORS = [
  {
    key: 'pos-tile-1',
    labelKey: 'colors.coffee',
    className: 'bg-pos-tile-1 text-pos-tile-1-foreground',
  },
  {
    key: 'pos-tile-2',
    labelKey: 'colors.mint',
    className: 'bg-pos-tile-2 text-pos-tile-2-foreground',
  },
  {
    key: 'pos-tile-3',
    labelKey: 'colors.tangerine',
    className: 'bg-pos-tile-3 text-pos-tile-3-foreground',
  },
  {
    key: 'pos-tile-4',
    labelKey: 'colors.ocean',
    className: 'bg-pos-tile-4 text-pos-tile-4-foreground',
  },
  {
    key: 'pos-tile-5',
    labelKey: 'colors.sun',
    className: 'bg-pos-tile-5 text-pos-tile-5-foreground',
  },
  {
    key: 'pos-tile-6',
    labelKey: 'colors.slate',
    className: 'bg-pos-tile-6 text-pos-tile-6-foreground',
  },
] as const

export const GRID_LIMITS = {
  columns: { min: 3, max: 10 },
  rows: { min: 3, max: 10 },
} as const

/**
 * What we know about the product behind a tile.
 *
 * This is a union rather than `Product | undefined` on purpose: absence of data is not a fact about
 * the product. The original bug was a truncated lookup handing `undefined` to a function that
 * reported product facts, which turned "not fetched" into "deactivated" and made a healthy
 * catalogue look wiped. A caller now has to say *which kind* of nothing it has.
 */
export type TileProduct =
  | { status: 'pending' }
  | { status: 'missing' }
  | { status: 'found'; product: Product }

export type TileState =
  | 'ready'
  | 'no-price'
  | 'deactivated'
  | 'open-price'
  | 'not-assigned'
  | 'unresolved'
  | 'pending'

/**
 * A tile only reaches the POS when its product is assigned to the restaurant, active and priced —
 * the bundle inner-joins assignments and prices (see `CatalogRepository.listByRestaurant`).
 * Every state other than `ready`/`open-price` means "this tile will not appear on the terminal".
 */
export function getTileState(tileProduct: TileProduct): TileState {
  if (tileProduct.status === 'pending') return 'pending'
  if (tileProduct.status === 'missing') return 'unresolved'
  const { product } = tileProduct
  if (product.assignedHere === false) return 'not-assigned'
  if (!product.active) return 'deactivated'
  if (product.openPrice) return 'open-price'
  if (product.currentPrice === undefined || product.currentPrice === 0)
    return 'no-price'
  return 'ready'
}

/**
 * Resolve a tile's SKU against a lookup result.
 *
 * @param isPending the lookup has not answered yet — distinct from having answered "no such SKU".
 */
export function resolveTileProduct(
  sku: string,
  products: Map<string, Product>,
  isPending: boolean,
): TileProduct {
  const product = products.get(sku)
  if (product) return { status: 'found', product }
  return isPending ? { status: 'pending' } : { status: 'missing' }
}

/** States that keep a tile off the POS terminal, i.e. worth warning about before saving. */
export function isBlockingTileState(state: TileState): boolean {
  return state !== 'ready' && state !== 'open-price' && state !== 'pending'
}

/**
 * A checkout add-on must be sellable outright — unlike a tile, there is no "save anyway" path for
 * it, so `open-price` and `pending` do not get the tile's pass: an add-on step cannot show a
 * stepper for a price the customer has to type in, and saving is expected to wait for the lookup
 * to resolve before judging eligibility at all (see `pending` handling at the call site).
 */
export function isAddonEligibleState(state: TileState): boolean {
  return state === 'ready'
}

/** Configured add-on SKUs that are not eligible to save, in configured order. */
export function findIneligibleAddonSkus(
  addonSkus: Array<string>,
  resolve: (sku: string) => TileProduct,
): Array<string> {
  return addonSkus.filter(
    (sku) => !isAddonEligibleState(getTileState(resolve(sku))),
  )
}

export function tileColorClass(colorKey: string) {
  const match = TILE_COLORS.find((color) => color.key === colorKey)
  return match ? match.className : 'bg-muted text-muted-foreground'
}

export function newId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `id-${Date.now()}-${Math.floor(Math.random() * 10000)}`
}

export function clampGridValue(value: number, min: number, max: number) {
  if (Number.isNaN(value)) return min
  return Math.max(min, Math.min(max, value))
}
